import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RequestDetails } from "@/components/RequestDetailsPanel";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const VERB = { endorse: "Endorse", authorize: "Authorize", verify: "Verify", final: "Final approve" };
const QUICK = ["Bill not clear/valid", "Invoice number/date missing", "Amount mismatch", "Wrong budget line", "GST/TDS issue", "Other"];
const FIX = [["project", "Project"], ["budget_line", "Head / Sub-type"], ["amount", "Amount"], ["expense_month", "Expense Month"], ["particulars", "Line items"],
  ["invoice_number", "Vendor bill no."], ["invoice_date", "Bill date"], ["vendor", "Vendor"], ["purpose", "Purpose"], ["expected_settlement_date", "Expected settlement"], ["notes", "Notes"], ["attachments", "Attachments"]];
const inr = (n) => `₹ ${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const Err = ({ msg, id }) => (msg ? <p className="text-xs text-rose-600 mt-1" data-testid={id}>{msg}</p> : null);


function TdsFields({ c, t, setT, errors }) {
  const tds = c.tds;
  if (tds.mode === "none") return <p className="text-xs text-slate-600 rounded bg-slate-50 p-2" data-testid="chain-dlg-tds-none">{tds.note}</p>;
  const base = tds.mode === "vendor" ? tds.base : c.amount;
  const total = tds.mode === "vendor" ? tds.invoice_total : c.amount;
  const tdsAmt = Math.round(base * (parseFloat(t.rate) || 0)) / 100;
  const pick = (v) => { const s = tds.sections.find(x => x.value === v); setT({ ...t, section: v, rate: s ? s.rate : 0 }); };
  const opts = tds.mode === "reimbursement" ? [{ value: "NONE", label: "No TDS (0%)", rate: 0 }, ...tds.sections] : tds.sections;
  return (
    <div className="rounded-md border p-3 space-y-2" data-testid="chain-dlg-tds">
      <div className="text-xs uppercase tracking-wider text-slate-500">TDS</div>
      <p className="text-xs text-slate-500">{tds.note}{tds.master_section ? ` Payee default (Masters): ${tds.master_section}.` : ""}</p>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>TDS Section</Label>
          <Select value={t.section} onValueChange={pick}>
            <SelectTrigger data-testid="chain-dlg-tds-section"><SelectValue placeholder="Select section" /></SelectTrigger>
            <SelectContent>{opts.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
          </Select>
          <Err msg={errors.section} id="chain-dlg-tds-section-error" />
        </div>
        <div><Label>TDS %</Label>
          <Input type="number" step="0.01" value={t.rate} onChange={e => setT({ ...t, rate: e.target.value })} data-testid="chain-dlg-tds-rate" />
          <Err msg={errors.rate} id="chain-dlg-tds-rate-error" />
        </div>
      </div>
      {tds.mode === "vendor" && (
        <div><Label>Reason {needsReason(c, t) ? "(required)" : "(if 0% or overridden)"}</Label>
          <Input value={t.reason} onChange={e => setT({ ...t, reason: e.target.value })} placeholder="e.g. Below threshold / exempt" data-testid="chain-dlg-tds-reason" />
          <Err msg={errors.reason} id="chain-dlg-tds-reason-error" />
        </div>
      )}
      <div className="text-sm bg-slate-50 rounded p-2" data-testid="chain-dlg-net-payable">TDS {inr(tdsAmt)} · Net payable <b>{inr(total - tdsAmt)}</b></div>
    </div>
  );
}

const needsReason = (c, t) => {
  const s = c.tds.sections.find(x => x.value === t.section);
  const r = parseFloat(t.rate);
  return r === 0 || (s && Math.abs((s.rate || 0) - (r || 0)) > 0.01);
};

function validate(c, t, onBehalf, comment, decision) {
  const e = {};
  if (decision !== "approve") { if (!comment.trim()) e.comment = "A reason is required."; return e; }
  if (c.on_behalf_required && !onBehalf) e.onBehalf = "Tick this box to continue.";
  if (c.current === "verify" && c.tds.mode === "vendor") {
    if (!t.section) e.section = "Pick a TDS section.";
    if (t.rate === "" || isNaN(parseFloat(t.rate))) e.rate = "Enter TDS %.";
    if (t.section && needsReason(c, t) && !t.reason.trim()) e.reason = "Reason needed for 0% or an overridden rate.";
  }
  return e;
}

const bodyFor = (c, t, onBehalf, comment) => {
  const b = { step: c.current, comment, on_behalf_confirmed: onBehalf };
  if (c.current !== "verify") return b;
  if (c.tds.mode === "vendor") return { ...b, tds_section_code: t.section, tds_rate: parseFloat(t.rate), tds_reason: t.reason };
  if (c.tds.mode === "reimbursement") return { ...b, tds_section_id: t.section === "NONE" ? null : t.section, tds_rate: parseFloat(t.rate) || 0, tds_reason: t.reason };
  return b;
};

export default function ChainActionDialog({ target, onClose, onDone }) {
  const [c, setC] = useState(null);
  const [t, setT] = useState({ section: "", rate: 0, reason: "" });
  const [fixFields, setFixFields] = useState([]);
  const [comment, setComment] = useState("");
  const [onBehalf, setOnBehalf] = useState(false);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!target) { setC(null); return; }
    setComment(""); setOnBehalf(false); setErrors({}); setFixFields([]);
    api.get(`/chain/${target.kind}/${target.id}/context`).then(r => {
      const d = r.data; setC(d);
      const td = d.tds || {};
      setT({ section: td.mode === "reimbursement" ? (td.default_section || "NONE") : (td.default_section || ""), rate: td.default_rate ?? 0, reason: "" });
    }).catch(e => { toast.error(e.response?.data?.detail || "Could not load request"); onClose(); });
  }, [target]);

  const submit = async (decision) => {
    const e = validate(c, t, onBehalf, comment, decision);
    setErrors(e);
    if (Object.keys(e).length) return;
    setBusy(true);
    try {
      const base = `/chain/${c.kind}/${c.id}`;
      if (decision === "approve") await api.post(`${base}/act`, bodyFor(c, t, onBehalf, comment));
      else await api.post(`${base}/${decision}`, { reason: comment, fields: decision === "send-back" ? fixFields : [] });
      toast.success(`${c.reference}: ${decision === "approve" ? VERB[c.current] + " done" : decision === "reject" ? "rejected" : "sent back"}`);
      onDone && onDone(); onClose();
    } catch (err) { setErrors({ form: err.response?.data?.detail || "Action failed" }); }
    finally { setBusy(false); }
  };

  const verb = VERB[c?.current] || "Approve";
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] p-0 gap-0 flex flex-col overflow-hidden" data-testid="chain-action-dialog">
        {!c ? <div className="py-10 text-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline" /></div> : (
          <>
            <DialogHeader className="px-6 pt-5 pb-3 border-b"><DialogTitle data-testid="chain-dlg-title">{verb} · {c.reference}</DialogTitle></DialogHeader>
            <div className="flex-1 overflow-y-auto overscroll-contain px-6 py-4 space-y-3" data-testid="chain-dlg-body">
            {c.routing_note && <p className="text-xs text-amber-700">{c.routing_note}</p>}
            {c.combined_flag && <p className="text-xs text-rose-700">Combined claims this month ≥ limit – Super Admin final approval required</p>}
            <RequestDetails c={c} />
            {c.current === "verify" && <TdsFields c={c} t={t} setT={setT} errors={errors} />}
            {c.on_behalf_required && (
              <div>
                <label className="flex items-center gap-2 text-sm" data-testid="chain-dlg-on-behalf-label">
                  <input type="checkbox" checked={onBehalf} onChange={e => setOnBehalf(e.target.checked)} data-testid="chain-dlg-on-behalf" />
                  Endorsed on behalf of PM – supporting documents checked
                </label>
                <Err msg={errors.onBehalf} id="chain-dlg-on-behalf-error" />
              </div>
            )}
            <div>
              <Label>Note / reason <span className="text-slate-400">(required for Send back / Reject)</span></Label>
              <div className="flex flex-wrap gap-1 my-1" data-testid="chain-dlg-quick-reasons">
                {QUICK.map(q => <button type="button" key={q} onClick={() => setComment(comment ? `${comment}; ${q}` : q)} className="text-[11px] px-2 py-0.5 rounded-full border hover:bg-slate-50" data-testid={`chain-dlg-quick-${q.split(" ")[0].toLowerCase()}`}>{q}</button>)}
              </div>
              <div className="flex flex-wrap gap-x-3 gap-y-1 mb-1 text-[11px] text-slate-600" data-testid="chain-dlg-fix-fields">
                <span>Needs correction:</span>
                {FIX.map(([k, l]) => <label key={k} className="flex items-center gap-1"><input type="checkbox" checked={fixFields.includes(k)} onChange={e => setFixFields(e.target.checked ? [...fixFields, k] : fixFields.filter(x => x !== k))} data-testid={`chain-dlg-fix-${k}`} />{l}</label>)}
              </div>
              <Textarea rows={2} value={comment} onChange={e => setComment(e.target.value)} data-testid="chain-dlg-comment" />
              <Err msg={errors.comment} id="chain-dlg-comment-error" />
            </div>
            <Err msg={errors.form} id="chain-dlg-error" />
            {!c.can_act && <p className="text-xs text-slate-500">You are not the assigned approver for this step.</p>}
            </div>
            <DialogFooter className="gap-2 flex-wrap px-6 py-3 border-t bg-white" data-testid="chain-dlg-footer">
              <Button variant="outline" disabled={busy || !c.can_act} onClick={() => submit("send-back")} data-testid="chain-dlg-sendback">Send back</Button>
              <Button variant="outline" className="border-red-200 text-red-700" disabled={busy || !c.can_act} onClick={() => submit("reject")} data-testid="chain-dlg-reject">Reject</Button>
              <Button className="bg-emerald-600 hover:bg-emerald-700" disabled={busy || !c.can_act} onClick={() => submit("approve")} data-testid="chain-dlg-confirm">
                {busy ? "Saving…" : verb}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
